import React, { useState, useEffect, useRef } from 'react';
import './TutorialPopup.css';

function TutorialPopup({ tutorial, tutorials, currentStep, onClose, onMarkViewed, onNext, highlightElementId }) {
    const [neverShowAgain, setNeverShowAgain] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [arrowContainerStyle, setArrowContainerStyle] = useState({});
    const [arrowSvgStyle, setArrowSvgStyle] = useState({});
    const popupRef = useRef(null);
    
    // Support both single tutorial and sequential tutorials
    const isSequential = tutorials && tutorials.length > 0;
    const currentTutorial = isSequential ? tutorials[currentStep] : tutorial;
    const isLastStep = isSequential && currentStep === tutorials.length - 1;

    // Add/remove highlight class to element and position arrow
    // NOTE: useEffect MUST be called unconditionally — guard comes after all hooks
    useEffect(() => {
        const elementId = isSequential ? tutorials[currentStep]?.highlightElementId : highlightElementId;
        
        if (elementId) {
            const element = document.getElementById(elementId);
            
            if (element) {
                element.classList.add('tutorial-highlight');
                
                // Position arrow after a short delay to ensure popup is rendered
                const positionArrow = () => {
                    if (!popupRef.current) return;
                    
                    const popupRect = popupRef.current.getBoundingClientRect();
                    const elementRect = element.getBoundingClientRect();
                    
                    // Calculate center points
                    const popupCenterX = popupRect.left + popupRect.width / 2;
                    const popupCenterY = popupRect.top + popupRect.height / 2;
                    const elementCenterX = elementRect.left + elementRect.width / 2;
                    const elementCenterY = elementRect.top + elementRect.height / 2;
                    
                    // Calculate angle from element to popup
                    const angle = Math.atan2(
                        popupCenterY - elementCenterY,
                        popupCenterX - elementCenterX
                    ) * (180 / Math.PI);
                    
                    // Position arrow next to the highlighted element, pointing toward it
                    const distance = 60; // Distance from element edge
                    const arrowX = elementCenterX + Math.cos(angle * Math.PI / 180) * (elementRect.width / 2 + distance);
                    const arrowY = elementCenterY + Math.sin(angle * Math.PI / 180) * (elementRect.height / 2 + distance);
                    
                    // Rotate arrow to point toward element (add 180 to flip direction, then adjust for SVG orientation)
                    const rotation = angle + 90;
                    
                    // Separate container positioning from SVG rotation to avoid CSS animation conflicts
                    setArrowContainerStyle({
                        left: `${arrowX}px`,
                        top: `${arrowY}px`,
                        transform: `translate(-50%, -50%)`
                    });
                    
                    setArrowSvgStyle({
                        transform: `rotate(${rotation}deg)`
                    });
                };
                
                // Position arrow initially and on window resize
                setTimeout(positionArrow, 100);
                window.addEventListener('resize', positionArrow);
                
                return () => {
                    element.classList.remove('tutorial-highlight');
                    window.removeEventListener('resize', positionArrow);
                };
            }
        }
    }, [currentStep, highlightElementId, isSequential, tutorials]);

    // Guard: AFTER all hooks — if currentTutorial is undefined, caller passed bad data
    if (!currentTutorial) {
        console.error('TutorialPopup: currentTutorial is undefined. tutorial=', tutorial, 'tutorials=', tutorials, 'currentStep=', currentStep);
        return null;
    }
    
    const handleClose = async () => {
        setIsClosing(true);
        
        try {
            // Mark tutorial as viewed (only on last step for sequential)
            if (onMarkViewed && (!isSequential || isLastStep)) {
                const identifier = currentTutorial?.page_identifier || 'tutorial';
                await onMarkViewed(identifier, neverShowAgain);
            }
        } catch (error) {
            console.error('Error marking tutorial as viewed:', error);
        }
        
        // Close after brief delay for animation
        setTimeout(() => {
            // If sequential and not last step, go to next
            if (isSequential && !isLastStep && onNext) {
                onNext();
            } else {
                onClose();
            }
        }, 200);
    };
    
    return (
        <>
            <div className="tutorial-overlay" onClick={handleClose} />
            <div ref={popupRef} className={`tutorial-popup ${isClosing ? 'closing' : ''}`}>
                {/* Header with checkbox and close button */}
                <div className="tutorial-header">
                    <label className="tutorial-never-show">
                        <input
                            type="checkbox"
                            checked={neverShowAgain}
                            onChange={(e) => setNeverShowAgain(e.target.checked)}
                        />
                        <span className="tutorial-text">Never show tutorials again</span>
                    </label>
                    
                    <button
                        className="tutorial-next-btn"
                        onClick={handleClose}
                        aria-label={isSequential && !isLastStep ? "Next tutorial" : "Close tutorial"}
                    >
                        {isSequential && !isLastStep ? "NEXT" : "CLOSE"}
                    </button>
                </div>
                
                {/* Content area */}
                <div className="tutorial-content">
                    {currentTutorial.image_url && (
                        <img
                            src={currentTutorial.image_url}
                            alt={currentTutorial.title}
                            className="tutorial-image"
                        />
                    )}
                    
                    <div className="tutorial-text tutorial-body">
                        {currentTutorial.title && <h2>{currentTutorial.title}</h2>}
                        <div dangerouslySetInnerHTML={{ __html: currentTutorial.content_html || currentTutorial.content_text }} />
                    </div>
                    
                    {/* Step indicator for sequential tutorials */}
                    {isSequential && (
                        <div className="tutorial-step-indicator">
                            Step {currentStep + 1} of {tutorials.length}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Yellow arrow pointing to highlighted element */}
            {(isSequential ? tutorials[currentStep]?.highlightElementId : highlightElementId) && (
                <div className="tutorial-arrow" style={arrowContainerStyle}>
                    <svg viewBox="0 0 24 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={arrowSvgStyle}>
                        <path
                            d="M12 2L12 46M12 46L5 39M12 46L19 39"
                            stroke="#FFD700"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            )}
        </>
    );
}

export default TutorialPopup;